'use strict';
import EventHelper from '../insights/helper/EventHelper';
import ApiConfig, { SEARCH_BASE, ECOM_DEV_SEARCH_BASE, ECOM_SEARCH_BASE, ECOM_ZOOVU_SEARCH_BASE, ECOM_STAGE_SEARCH_BASE } from '../siteSearch/api/ApiConfig';
import SxQueryUtils from '../sxQuery/SxQueryUtils';
import SearchResponse from '../siteSearch/model/SearchResponse';
import sxQuery from '../sxQuery/sxQuery';
import DataSourceType from '../insights/interface/DataSourceType';
import LayoutType from './enums/LayoutType';
import ResultStyle from './enums/ResultStyle';
import ImagePosition from './enums/ImagePosition';
import Icons from '../siteSearch/components/Icons';
import DisplayType from './enums/DisplayType';
import DrawerPosition from './enums/DrawerPosition';
import TemplateRenderer from '../global/TemplateRenderer';
import StringHelper from '../global/StringHelper';
import UiColors from '../siteSearch/styles/UiColors';
import Helper from '../siteSearch/utils/Helper';
import UiHelper from '../siteSearch/ui/UiHelper';
const EMPTY_STATE_ICON = Icons.getNoResultsIcon('ss360-related-content__empty-state-icon');
const CLOSE_BUTTON_ICON = `<svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M20.8907 6.31738L21.6825 7.10922L7.1102 21.6804L6.31836 20.8891L20.8907 6.31738Z" fill="${UiColors.THEME}"/><path d="M7.1102 6.31738L21.6825 20.8886L20.8907 21.681L6.31836 7.10978L7.1102 6.31738Z" fill="${UiColors.THEME}"/></svg>`;
const GRID_DEFAULTS = {
    sm: 1,
    md: 2,
    lg: 3,
    xl: 3
};
class RelatedContent {
    constructor(configuration, insights, isZoovu = false) {
        this.lastFocusedElement = null;
        this.configuration = configuration;
        this.insights = insights;
        if (this.configuration.limit === undefined) {
            this.configuration.limit = 3;
        }
        let baseUrl = SEARCH_BASE;
        let suggestBaseUrl;
        let isFullBaseUrl = false;
        if (configuration.ecomDev) {
            baseUrl = ECOM_DEV_SEARCH_BASE;
        }
        else if (configuration.ecom) {
            baseUrl = isZoovu ? ECOM_ZOOVU_SEARCH_BASE : ECOM_SEARCH_BASE;
        }
        else if (configuration.ecomStage) {
            baseUrl = ECOM_STAGE_SEARCH_BASE;
        }
        if (configuration.baseUrl !== undefined) {
            baseUrl = configuration.baseUrl;
            isFullBaseUrl = true;
        }
        if (configuration.querySource === undefined) {
            configuration.querySource = {
                type: DataSourceType.DomElement,
                selector: 'h1'
            };
        }
        if (configuration.querySource.type === undefined) {
            configuration.querySource.type = DataSourceType.DomElement;
        }
        if (configuration.layoutType === undefined) {
            configuration.layoutType = LayoutType.Grid;
        }
        if (configuration.showSnippet === undefined) {
            configuration.showSnippet = true;
        }
        if (configuration.showDataPoints === undefined) {
            configuration.showDataPoints = true;
        }
        if (configuration.resultStyle === undefined) {
            configuration.resultStyle = ResultStyle.Plain;
        }
        if (configuration.imagePosition === undefined) {
            configuration.imagePosition = configuration.layoutType === LayoutType.Grid ? ImagePosition.Top : ImagePosition.Left;
        }
        if (configuration.grid === undefined) {
            configuration.grid = Object.assign({}, GRID_DEFAULTS);
        }
        else {
            Object.keys(GRID_DEFAULTS).forEach((key) => {
                // @ts-ignore
                if (configuration.grid[key] === undefined) {
                    // @ts-ignore
                    configuration.grid[key] = GRID_DEFAULTS[key];
                }
            });
        }
        if (configuration.active === undefined) {
            configuration.active = true;
        }
        if (configuration.querySource === undefined) {
            configuration.querySource = {
                type: DataSourceType.DomElement,
                selector: 'h1'
            };
        }
        if (configuration.title === undefined) {
            configuration.title = 'Related content';
        }
        if (configuration.showRelationTitle === undefined) {
            configuration.showRelationTitle = true;
        }
        if (configuration.showCloseIcon === undefined) {
            configuration.showCloseIcon = false;
        }
        if (configuration.showLoader === undefined) {
            configuration.showLoader = false;
        }
        if (this.configuration.includeRelations !== undefined) {
            this.configuration.includeRelations.forEach((relation) => {
                if (relation.limit === undefined) {
                    relation.limit = this.configuration.limit;
                }
                relation.limit = Math.max(Math.min(relation.limit, 24), 1);
            });
        }
        if (this.configuration.includeTags !== undefined) {
            this.configuration.includeTags.forEach((tag) => {
                if (tag.limitPerRelation === undefined) {
                    tag.limitPerRelation = 2;
                }
                tag.limitPerRelation = Math.max(Math.min(tag.limitPerRelation, 10), 1);
            });
        }
        if (this.configuration.displayType === undefined) {
            this.configuration.displayType = DisplayType.Inline;
        }
        if (this.configuration.drawerPosition === undefined) {
            this.configuration.drawerPosition = DrawerPosition.Right;
        }
        this.renderer = this.configuration.renderer;
        this.apiConfig = new ApiConfig(baseUrl, configuration.ecomDev || configuration.ecom, configuration.siteId, isFullBaseUrl, suggestBaseUrl);
        this.appendStyles();
        if (configuration.active) {
            // @ts-ignore
            console.log(`Initializing ${isZoovu ? 'Zoovu' : 'SiteSearch360'} ${SS360_VERSION} related content component`);
        }
    }
    appendStyles() {
        let css = require('../../sass//siteSearch/related_content.scss'); // load the default css
        if (this.configuration.themeColor !== undefined) {
            css += `.ss360-related-content__link{color: ${this.configuration.themeColor}}`;
        }
        if (this.configuration.layoutType === LayoutType.Grid) {
            const { grid } = this.configuration;
            const wereBreakpointsModified = Object.keys(grid).reduce((acc, key) => {
                // @ts-ignore
                return acc || GRID_DEFAULTS[key] !== grid[key];
            }, false);
            if (wereBreakpointsModified) {
                const keys = ['xl', 'lg', 'md', 'sm'];
                keys.forEach((key) => {
                    // @ts-ignore
                    const gridCols = new Array(grid[key] + 1).join('1fr ');
                    const gridCss = `.ss360-related-content__content--grid {-ms-grid-columns:${gridCols};grid-template-columns:${gridCols}}`;
                    if (key === 'xl') {
                        css += gridCss;
                    }
                    else {
                        let breakpoint = '@media(max-width:1199px)';
                        if (key === 'md') {
                            breakpoint = '@media(max-width:991px)';
                        }
                        else if (key === 'sm') {
                            breakpoint = '@media(max-width:767px)';
                        }
                        css += `${breakpoint}{${gridCss}}`;
                    }
                });
            }
        }
        const $styleTarget = this.configuration.styleTarget ? sxQuery(this.configuration.styleTarget) : sxQuery('head');
        $styleTarget.find('#ss360-related-content-styles').remove();
        $styleTarget.prepend(`<style id="related-content-styles" type="text/css">${css}</style>`);
    }
    loadAndRender(animationDuration = 300, originalQuery, scrollIntoView = false, isSerp = false) {
        if (this.configuration.urlPattern !== undefined && this.configuration.urlPattern.length > 0 && window.location.href.match(new RegExp(this.configuration.urlPattern)) === null) {
            return;
        }
        if (!this.configuration.active) {
            return;
        }
        const query = EventHelper.getDataSourceValue(this.configuration.querySource);
        if (query === undefined || query === null || query.trim().length === 0) {
            return;
        }
        this.lastFocusedElement = document.activeElement;
        const loadStartTime = new Date().getTime();
        const qs = this.apiConfig.baseUrl.indexOf('?') !== -1 ? '&' : '?';
        let queryUrl = `${this.apiConfig.baseUrl}${qs}site=${this.configuration.siteId}&query=${encodeURIComponent(query)}&limit=${this.configuration.limit + 1}&filterOptions=false&offset=0&kvtable=false&includeContent=true&source=related_content`;
        if (this.configuration.isRelationSearch) {
            const isHttps = this.apiConfig.baseUrl.indexOf('https') === 0;
            const domain = this.apiConfig.baseUrl.replace(/https?:\/\//, '').split('/')[0];
            const logQueries = this.configuration._logQueries !== undefined ? this.configuration._logQueries : true;
            queryUrl = `http${isHttps ? 's' : ''}://${domain}/search/relations/result?projectId=${this.configuration.siteId}&identifier=${encodeURIComponent(query)}&limit=${this.configuration.limit}&log=${logQueries}`;
            if (this.configuration.includeRelations !== undefined && this.configuration.includeRelations.length > 0) {
                queryUrl = `${queryUrl}&includeRelations=${encodeURIComponent(JSON.stringify(this.configuration.includeRelations))}`;
            }
            if (this.configuration.includeTags !== undefined && this.configuration.includeTags.length > 0) {
                queryUrl = `${queryUrl}&includeTags=${encodeURIComponent(JSON.stringify(this.configuration.includeTags))}`;
            }
        }
        else if (this.configuration.isVisualSimilaritySearch) {
            const isHttps = this.apiConfig.baseUrl.indexOf('https') === 0;
            const domain = this.apiConfig.baseUrl.replace(/https?:\/\//, '').split('/')[0];
            queryUrl = `http${isHttps ? 's' : ''}://${domain}/products/search/article/visually-similar?projectId=${this.configuration.siteId}&articleNumber=${encodeURIComponent(query)}&number=${this.configuration.limit}&diff=false&limit-category=true`;
        }
        else if (this.configuration.contentGroups !== undefined && this.configuration.contentGroups.length > 0) {
            queryUrl += `&includeContentGroups=${encodeURIComponent(JSON.stringify(this.configuration.contentGroups))}`;
        }
        if ((this.configuration.userGroup || '').length > 0) {
            queryUrl += `&userGroup=${encodeURIComponent(this.configuration.userGroup)}`;
        }
        const logQueries = this.configuration._logQueries !== false && SxQueryUtils.readCookie('ss360-tracking') !== '0' && SxQueryUtils.readCookie('zoovu-tracking') !== '0';
        queryUrl += `&log=${logQueries}`;
        if (this.configuration._cacheQueries === false) {
            queryUrl += '&caching=false&cache=false';
        }
        if (this.configuration.queryTag) {
            queryUrl += `&queryTag=${encodeURIComponent(this.configuration.queryTag)}`;
        }
        let resultsLoaded = false;
        let wasWrapperCreated = false;
        const onModalRendered = () => {
            if (this.configuration.displayType !== DisplayType.Drawer && this.configuration.displayType !== DisplayType.Layover) {
                return;
            }
            SxQueryUtils.requestAnimationFrame(() => {
                UiHelper.trapFocus('relationsFocus', () => this.getTarget().getFocusableElements(), true);
                setTimeout(() => {
                    UiHelper.trapFocus('relationsFocus', () => this.getTarget().getFocusableElements(), true);
                }, 100);
            });
            sxQuery('body').off('keydown.closeRelations').on('keydown.closeRelations', (e) => {
                if (e.keyCode === 27 || e.which === 27 || e.key === 'Escape' || e.code === 'Escape') {
                    e.stopPropagation();
                    this._onClose();
                }
            });
        };
        if (this.configuration.showLoader) {
            setTimeout(() => {
                if (!resultsLoaded) {
                    const loaderSection = this._createWrap('ss360-related-content--loading', 'aria-busy="true"');
                    loaderSection.append('<span class="ss360-related-content__main-title ss360-related-content__main-title--loading" role="presentation"></span>');
                    const resultSection = sxQuery(`<div class="ss360-related-content__content ss360-related-content__content--loading ss360-related-content__content--${this.configuration.layoutType}" role="presentation"></div>`);
                    for (let i = 0; i < this.configuration.limit; i++) {
                        resultSection.append('<article class="ss360-related-content__item ss360-related-content__item--loading" role="presentation"></article>');
                    }
                    loaderSection.append(resultSection);
                    const $target = this.getTarget();
                    $target.append(loaderSection);
                    this._appendBackdrop($target);
                    this._hideBodyOverflow();
                    loaderSection.slideDown(animationDuration);
                    wasWrapperCreated = true;
                    onModalRendered();
                }
            }, 50);
        }
        SxQueryUtils.get(queryUrl, (data) => {
            resultsLoaded = true;
            const searchResponses = [];
            if (!this.configuration.isRelationSearch) {
                searchResponses.push(new SearchResponse(data));
            }
            else if ((data.relations || []).length > 0 || (data.tags || []).length > 0) { // map to the required structure
                (data.tags || []).forEach((tag) => {
                    const results = [];
                    (tag.relations || []).forEach((relation) => {
                        (relation.searchResult || []).forEach((searchResult) => {
                            results.push(searchResult);
                        });
                    });
                    // @ts-ignore
                    const searchResponse = new SearchResponse({
                        searchResults: [{
                                results,
                                name: '_',
                                type: 'other',
                                totalResults: results.length
                            }]
                    });
                    const tagConfiguration = (this.configuration.includeTags || []).filter((otherTag) => otherTag.tag === tag.tag)[0];
                    if (tagConfiguration !== undefined && tagConfiguration.title !== undefined && tagConfiguration.title.length > 0) {
                        searchResponse.setResultTitle(tagConfiguration.title);
                    }
                    else {
                        searchResponse.setResultTitle(tag.tag);
                    }
                    searchResponse.setRelationTag(tag.tag);
                    searchResponses.push(searchResponse);
                });
                (data.relations || []).forEach((relation) => {
                    // @ts-ignore
                    const searchResponse = new SearchResponse({
                        searchResults: [{
                                results: relation.searchResult || [],
                                name: '_',
                                type: 'other',
                                totalResults: (relation.searchResult || []).length
                            }
                        ]
                    });
                    searchResponse.setResultTitle(relation.name);
                    searchResponse.setRelationId((relation.id || -1).toString());
                    searchResponses.push(searchResponse);
                });
            }
            let hasContent = false;
            searchResponses.forEach((searchResponse) => {
                const result = this.renderSearchResponse(searchResponse, query, loadStartTime, Math.round(Math.random() * 1000000).toString(), originalQuery, isSerp, wasWrapperCreated);
                hasContent || (hasContent = result);
            });
            if (!hasContent && (this.configuration.emptyStateText || '').length > 0) {
                this._renderEmptyState();
            }
            if (scrollIntoView) {
                const $contentBlock = this.getTarget().get(0);
                if ($contentBlock !== undefined && $contentBlock.scrollIntoView) {
                    $contentBlock.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                }
            }
            onModalRendered();
        }, () => {
            if (this.configuration.showLoader) {
                this.getTarget().find('.ss360-related-content--loading').remove();
                this._renderEmptyState();
            }
        }, undefined, false);
    }
    _renderEmptyState() {
        const wrap = this._createWrap('ss360-related-content--empty-state');
        if ((this.configuration.title || '').length > 0) {
            this._renderTitle(wrap, this.configuration.title, this.configuration.showCloseIcon);
        }
        const emptyStateWrap = sxQuery('<div class="ss360-related-content__empty-state-wrap"></div>');
        const emptyStateTextWrap = sxQuery('<div class="ss360-related-content__text-wrap"></div>');
        if ((this.configuration.emptyStateTitle || '').length > 0) {
            emptyStateTextWrap.append(`<span class="ss360-related-content__empty-state-title" aria-level="3" role="heading">${this.configuration.emptyStateTitle}</span>`);
        }
        emptyStateTextWrap.append(`<p class="ss360-related-content__empty-state">${this.configuration.emptyStateText}</p>`);
        emptyStateWrap.append(EMPTY_STATE_ICON);
        emptyStateWrap.append(emptyStateTextWrap);
        wrap.append(emptyStateWrap);
        const $target = this.getTarget();
        $target.append(wrap);
        this._appendBackdrop($target);
        this._hideBodyOverflow();
    }
    _createWrap(modifierClass = '', attrs = '', wrapWasCreated = false, ariaLabel = 'Related Content') {
        if (modifierClass.length > 0) {
            modifierClass = ` ${modifierClass}`;
        }
        if (attrs.length > 0) {
            attrs = ` ${attrs}`;
        }
        const $wrap = sxQuery(`<section class="ss360-related-content ss360-related-content--${this.configuration.displayType} ss360-related-content--${this.configuration.layoutType}${modifierClass}" ${attrs}></section>`);
        $wrap.attr('aria-label', this.configuration.product && this.configuration.product.getName() ? `${ariaLabel} for ${this.configuration.product.getName()}` : ariaLabel);
        if (this.configuration.displayType === DisplayType.Drawer) {
            $wrap.addClass(`ss360-related-content--drawer--${this.configuration.drawerPosition}`);
            const openClass = 'ss360-related-content--drawer--open';
            if (!wrapWasCreated) {
                SxQueryUtils.requestAnimationFrame(() => {
                    $wrap.addClass(openClass);
                });
            }
            else {
                $wrap.addClass(openClass);
            }
        }
        if (this.configuration.displayType === DisplayType.Layover || this.configuration.displayType === DisplayType.Drawer) {
            $wrap.attr('role', 'dialog');
            $wrap.attr('aria-modal', 'true');
        }
        else {
            $wrap.attr('role', null);
            $wrap.attr('aria-modal', null);
        }
        return $wrap;
    }
    _renderTitle(wrap, title, appendCloseButton = false) {
        const $closeButton = appendCloseButton ? sxQuery(`<button class="ss360-n-button ss360-related-content__close" aria-label="Close">${CLOSE_BUTTON_ICON}</button>`) : undefined;
        if ($closeButton !== undefined) {
            $closeButton.on('click', () => {
                this._onClose();
            });
        }
        const product = this.configuration.product;
        if (product !== undefined && (this.configuration.displayType === DisplayType.Drawer || this.configuration.displayType === DisplayType.Layover)) {
            const $header = sxQuery('<header class="ss360-n-section ss360-related-content__header ss360-related-content__header--product"></header>');
            const $productWrap = sxQuery('<div class="ss360-related-content__product-wrap"></div>');
            const $productContentWrap = sxQuery('<div class="ss360-related-content__product-content-wrap"></div>');
            const $title = sxQuery(`<span class="ss360-related-content__main-title" aria-level="2" role="heading"></span>`);
            $title.text(product.getName());
            $productContentWrap.append($title);
            if (product.hasImage()) {
                const $image = sxQuery(`<img loading="lazy" src="${product.getImage()}" class="ss360-related-content__product-image" alt="">`);
                $productWrap.append($image);
            }
            $productWrap.append($productContentWrap);
            $header.append($productWrap);
            const priceDataPoint = Helper.getDataPoint(product, this.configuration.priceDataPoint, 'newPrice');
            const oldPriceDataPoint = Helper.getDataPoint(product, this.configuration.oldPriceDataPoint, 'oldPrice');
            if (priceDataPoint !== undefined) {
                const priceWrap = sxQuery('<div class="ss360-related-content__price-wrap"></div>');
                priceWrap.append(`<span class="ss360-related-content__price ss360-related-content__price--new">${priceDataPoint.value}</span>`);
                if (oldPriceDataPoint !== undefined) {
                    priceWrap.append(`<span class="ss360-related-content__price ss360-related-content__price--old">${oldPriceDataPoint.value}</span>`);
                }
                $productContentWrap.append(priceWrap);
            }
            if (this.configuration.addToCartLink || this.configuration.addToCartCallback) {
                const $addToCartCta = sxQuery('<button class="ss360-n-button ss360-cta ss360-cta--button ss360-cta--primary ss360-related-content__add-to-cart" type="button"></button>');
                $addToCartCta.append(Icons.getSvgIcon(Icons.SHOPPING_CART, UiColors.WHITE, 'ss360-cta__img', undefined, undefined, '0 0 17 16'));
                const $span = sxQuery('<span></span>');
                $span.text(this.configuration.addToCartLabel);
                $addToCartCta.append($span);
                $addToCartCta.on('click', (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (this.insights !== undefined) {
                        let price;
                        let priceUnit;
                        if (priceDataPoint !== undefined) {
                            const priceData = Helper.getPriceAndCurrency(priceDataPoint);
                            price = priceData.price;
                            priceUnit = priceData.currency;
                        }
                        this.insights.trackAddToCart(product.getIdentifier(), product.getIdentifier(), product.getLink(), 1, price, priceUnit, true, false, false);
                    }
                    if (this.configuration.addToCartCallback) {
                        this.configuration.addToCartCallback(product.getIdentifier(), product.plain);
                    }
                    else {
                        window.location.href = this.configuration.addToCartLink.replace(/#RESULT_LINK#/g, product.getLink()).replace(/#IDENTIFIER#/g, product.getIdentifier());
                    }
                });
                $productContentWrap.append($addToCartCta);
            }
            if (appendCloseButton) {
                $header.append($closeButton);
            }
            wrap.append($header);
            wrap.append('<hr class="ss360-related-content__divider">');
            wrap.append(`<span class="ss360-related-content__subtitle" aria-level="3" role="heading">${title}</span>`);
            return;
        }
        const titleMarkup = `<span class="ss360-related-content__main-title" aria-level="2" role="heading">${title}</span>`;
        if (appendCloseButton) {
            const $header = sxQuery(`<header class="ss360-n-section ss360-related-content__header">${titleMarkup}</header>`);
            $header.append($closeButton);
            wrap.append($header);
        }
        else {
            wrap.append(titleMarkup);
        }
    }
    renderSearchResponse(searchResponse, query, loadStartTime, id, originalQuery, isSerp = false, wasWrapperCreated = false) {
        this.getTarget().find('.ss360-related-content--loading').remove();
        let flatSuggests = [];
        searchResponse.suggests.forEach((resultGroup) => {
            resultGroup.suggests.forEach((suggest) => {
                flatSuggests.push(suggest);
            });
        });
        const patternRegex = /^https?:/;
        const cleanHref = window.location.href.replace(patternRegex, '');
        if (!this.configuration.isRelationSearch && !this.configuration.isVisualSimilaritySearch) {
            flatSuggests = flatSuggests.filter((suggest) => (suggest.getLink() || '').replace(patternRegex, '') !== cleanHref && (this.configuration.product === undefined || this.configuration.product.getLink() !== suggest.getLink()));
        }
        flatSuggests = flatSuggests.slice(0, this.configuration.limit);
        if (flatSuggests.length === 0) {
            return false;
        }
        let title = this.configuration.title;
        if ((this.configuration.showRelationTitle && (searchResponse.getRelationTag() !== undefined || searchResponse.getRelationId() !== undefined))
            || (searchResponse.getRelationTag() !== undefined && searchResponse.getResultTitle() !== searchResponse.getRelationTag())) {
            title = searchResponse.getResultTitle();
        }
        const wrap = this._createWrap(undefined, undefined, wasWrapperCreated, title);
        if (title !== undefined && title.length > 0) {
            this._renderTitle(wrap, title, this.configuration.showCloseIcon);
        }
        const contentWrap = sxQuery(`<div class="ss360-related-content__content ss360-related-content__content--${this.configuration.layoutType}"></div>`);
        const templateRenderer = this.configuration.resultTemplate !== undefined ? new TemplateRenderer(this.configuration.resultTemplate, false) : undefined;
        flatSuggests.forEach((suggest, idx) => {
            suggest.isRelation = true;
            if (templateRenderer !== undefined) {
                contentWrap.append(templateRenderer.render(suggest, undefined, false, false, true));
                return;
            }
            if (this.renderer !== undefined) {
                const entry = this.renderer.render(suggest, false, undefined);
                const div = document.createElement('div');
                div.className = entry.className;
                div.dataset.productIdentifier = entry.dataset.productIdentifier;
                div.appendChild(entry.firstChild);
                const $div = sxQuery(div);
                $div.find('.ss360-actions').remove();
                $div.addClass('ss360-suggests--relation');
                $div.find('img').on('load', (e) => {
                    sxQuery(e.target).removeClass('ss360-shimmer');
                }).on('error', (e) => {
                    const $target = sxQuery(e.target);
                    $target.removeClass('ss360-shimmer');
                    $target.hide();
                    $target.parents('.ss360-suggests__image-wrap', true).addClass('ss360-suggests__image-wrap--empty');
                });
                contentWrap.append($div);
                if (this.insights !== undefined) {
                    $div.find('a:not(.ss360-js-add-to-cart):not(.ss360-js-exclude-tracking)').on('click', () => {
                        this.insights.trackRelatedContentClicked(query, id, suggest.getLink(), idx, searchResponse.getRelationId(), searchResponse.getRelationTag(), suggest.getIdentifier());
                    });
                }
                return;
            }
            const entry = sxQuery('<article class="ss360-related-content__item"></article>');
            if (this.configuration.resultStyle === ResultStyle.Card) {
                entry.addClass('ss360-related-content__item--card');
            }
            let contentTarget = entry;
            const identifier = StringHelper.escapeHtml(suggest.getIdentifier() || '');
            if (suggest.hasImage()) {
                entry.append(`<a class="ss360-related-content__image-link" href="${suggest.getLink()}" data-ss360-identifier="${identifier}" target="_blank"><img loading="lazy" src="${suggest.getImage()}" class="ss360-related-content__image"></a>`);
                if (this.configuration.imagePosition !== ImagePosition.Top) {
                    const contentWrap = sxQuery('<div class="ss360-related-content__content-wrap"></div>');
                    entry.append(contentWrap);
                    entry.addClass(`ss360-related-content__item--image-${this.configuration.imagePosition}`);
                    contentTarget = contentWrap;
                }
            }
            contentTarget.append(`<span role="heading" aria-lebel="3" class="ss360-related-content__title"><a href="${suggest.getLink()}" class="ss360-related-content__link" target="_blank" data-ss360-identifier="${identifier}">${suggest.getName()}</a></span>`);
            if (this.configuration.showSnippet) {
                contentTarget.append(`<p class="ss360-related-content__item-content">${suggest.getContent() || ''}</p>`);
            }
            if (this.configuration.showDataPoints) {
                const dataPoints = suggest.getDataPoints().filter((dp) => {
                    return (dp.show !== undefined ? dp.show : dp.type === 'PASS_ON') && (this.configuration.dataPoints === undefined || this.configuration.dataPoints.indexOf(dp.key) !== -1);
                }).reduce((acc, dp) => {
                    const { key, value } = dp;
                    if (acc[key] === undefined) {
                        acc[key] = [];
                    }
                    if (acc[key].indexOf(value) === -1) {
                        acc[key].push(value);
                    }
                    return acc;
                }, {});
                if (Object.keys(dataPoints).length > 0) {
                    const table = sxQuery('<table class="ss360-related-content__data-points"><tbody class="ss360-related-content__data-points-body"></tbody></table>');
                    const tbody = table.find('.ss360-related-content__data-points-body');
                    if (this.configuration.layoutType === LayoutType.List) {
                        table.addClass('ss360-related-content__data-points--row');
                    }
                    const trs = [];
                    const hiddenDataPointKeys = this.configuration.hiddenDataPointKeys || [];
                    const allKeysHidden = Object.keys(dataPoints).filter((key) => hiddenDataPointKeys.indexOf(key) === -1).length === 0;
                    Object.keys(dataPoints).forEach((key) => {
                        const keyClass = hiddenDataPointKeys.indexOf(key) !== -1 ? ' ss360-related-content__data-point-cell--hidden' : '';
                        const keyTemplate = allKeysHidden ? '' : `<td class="ss360-related-content__data-point-cell ss360-related-content__data-point-cell--key${keyClass}">${key}</td>`;
                        const tr = `<tr class="ss360-related-content__data-point ss360-related-content__data-point--${StringHelper.getSafeKey(key)}">
							${keyTemplate}
							<td class="ss360-related-content__data-point-cell ss360-related-content__data-point-cell--value">${dataPoints[key].join(', ')}</td>
						</tr>`;
                        trs.push(tr);
                    });
                    tbody.html(trs.join(''));
                    table.append(tbody);
                    contentTarget.append(table);
                }
            }
            contentWrap.append(entry);
        });
        wrap.append(contentWrap);
        const $target = this.getTarget();
        ;
        $target.append(wrap);
        this._hideBodyOverflow();
        if (this.configuration.displayType !== DisplayType.Inline) {
            this._appendBackdrop($target);
        }
        if (this.insights !== undefined) {
            const relationId = searchResponse.getRelationId();
            const relationTag = searchResponse.getRelationTag();
            wrap.onScrolledIntoViewport(() => {
                this.insights.trackRelatedContentViewed(query, id, relationId, relationTag);
            });
            this.insights.trackRelatedContentLoaded(query, new Date().getTime() - loadStartTime, flatSuggests.length, id, relationId, this.configuration.isVisualSimilaritySearch ? '_sx_visual-similarity' : relationTag, originalQuery, isSerp);
            sxQuery(wrap.find('.ss360-related-content__item')).each((node, idx) => {
                const $node = sxQuery(node).find('a[href]');
                const handler = () => {
                    this.insights.trackRelatedContentClicked(query, id, 
                    // @ts-ignore
                    $node.attr('href'), idx, relationId, relationTag, $node.data('ss360Identifier'));
                    $node.off('click', handler);
                };
                $node.on('click', handler);
            });
        }
        return true;
    }
    getTarget() {
        let $target;
        if (this.configuration.contentBlock === undefined || this.configuration.contentBlock === 'article, main, body') {
            $target = sxQuery('#ss360-related-content');
            $target = $target.length > 0 ? $target : sxQuery('article');
            $target = $target.length > 0 ? $target : sxQuery('main');
            $target = $target.length > 0 ? $target : sxQuery('body');
        }
        else {
            $target = sxQuery(sxQuery(this.configuration.contentBlock, true).get(0), true);
        }
        return $target;
    }
    _appendBackdrop($target) {
        $target.find('.ss360-related-content-backdrop').remove();
        if (this.configuration.displayType !== DisplayType.Inline) {
            const $backdrop = sxQuery('<div role="presentation" class="ss360-related-content-backdrop"><div>');
            $backdrop.on('click', () => {
                this._onClose();
            });
            $target.append($backdrop);
        }
    }
    _onClose() {
        if (this.configuration.onClose !== undefined) {
            const isDrawer = this.configuration.displayType === DisplayType.Drawer;
            if (isDrawer) {
                const $target = this.getTarget();
                $target.find('.ss360-related-content').removeClass('ss360-related-content--drawer--open');
                $target.find('.ss360-related-content-backdrop').addClass('ss360-related-content-backdrop--hide');
            }
            sxQuery(window).off('keydown.relationsFocus');
            sxQuery('body').off('keydown.closeRelations');
            if (this.lastFocusedElement !== undefined && this.lastFocusedElement !== null && this.lastFocusedElement !== document.body) {
                this.lastFocusedElement.focus();
                this.lastFocusedElement = null;
            }
            setTimeout(() => {
                this.configuration.onClose();
                sxQuery('body').removeClass('ss360-u-hide-y-overflow');
            }, isDrawer ? 300 : 0);
        }
        else {
            sxQuery('body').removeClass('ss360-u-hide-y-overflow');
        }
    }
    _hideBodyOverflow() {
        if (this.configuration.displayType !== DisplayType.Inline) {
            sxQuery('body').addClass('ss360-u-hide-y-overflow');
        }
    }
}
export default RelatedContent;
